import { useState } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

import { signIn, signUp } from "./auth";
import { colors } from "./theme";

// Shown when the user lands on /admin/ without a Cognito session.
// Replaces the previous "immediate redirect to Cognito" behavior — that
// dropped users on an unfamiliar domain with no context. This page gives
// them a moment to understand what they're signing into and then either
// log in (existing user) or sign up (new user).
export default function Landing() {
  const [busy, setBusy] = useState<"signin" | "signup" | null>(null);

  async function handleSignIn() {
    setBusy("signin");
    try { await signIn(); } catch { setBusy(null); }
  }

  async function handleSignUp() {
    setBusy("signup");
    try { await signUp(); } catch { setBusy(null); }
  }

  return (
    <Container sx={{ pt: 8 }}>
      <Paper elevation={1} sx={{ p: { xs: 3, sm: 5 }, maxWidth: 640, mx: "auto" }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: colors.gray900 }}>
              Agent Screen Recordings
            </Typography>
            <Typography variant="subtitle1" sx={{ color: colors.gray500, mt: 0.5 }}>
              Search, play back, and manage agent screen recordings.
            </Typography>
          </Box>

          <Typography variant="body1" sx={{ color: colors.gray700 }}>
            This dashboard is for supervisors and administrators. Agents who run
            the recorder helper authenticate through it without ever visiting
            this page — they sign up once for an account, then use the helper.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ pt: 1 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<LoginIcon />}
              onClick={handleSignIn}
              disabled={busy !== null}
              sx={{ flex: 1 }}
            >
              {busy === "signin" ? "Redirecting…" : "Sign in"}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={handleSignUp}
              disabled={busy !== null}
              sx={{ flex: 1 }}
            >
              {busy === "signup" ? "Redirecting…" : "Sign up"}
            </Button>
          </Stack>

          <Box
            sx={{
              mt: 2,
              p: 2,
              border: `1px solid ${colors.gray200}`,
              borderRadius: 1,
              background: colors.gray50,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.gray600, display: "block", lineHeight: 1.6 }}>
              <strong>New here?</strong> Sign-up requires a <code>@nwn.ai</code> or
              <code> @apcisg.com</code> email. After verifying your email, an
              administrator will be asked to approve your account before it can
              access the dashboard — you'll see a "pending approval" page until
              that happens.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Container>
  );
}
